import { Checkbox } from "construct-ui";
import moment from "moment";
import DatePickerComponent from "../../components/date-picker/date-picker.component";
import paginationComponent from "../../components/pagination/pagination.component";
import SingleSelectComponent from "../../components/single-select/single-select.component";
import SelectSiteSearchInputComponent from "../../components/site/select-site-search-input.component";
import SelectUserComponent from "../../components/user/select-user.component";
import { formats } from "../../config/constants";
import { downloadExcelGoodsApproval, getGoodsApprovalList, saveMultipleGoodsApproval } from "../../models/posm/goods-approval.model";
import POSMAuthorizationModel, { POSM_PERMISSION_KEY } from "../../models/posm/posm-authority.model";
import { ASCENDING_TEXT, DESCENDING_TEXT } from "../../models/posm/posm.constant";
import ViewBase from "../view-base.view";
import MultiplePOSMLevelSelect from "./posm-goods-issue-application/filters/multiple-posm-level-select.view";
import StorageLocationSelect from "./posm-goods-issue-application/filters/storage-location-select.view";
import { APPROVAL_STATUSES } from "./posm-goods-issue-application/posm-goods-issue-application.view";

/**
 * POSM Goods Approval
 * This function is used by the warehouse distributor to confirm actual goods issue from warehouse based on the approved Goods Issue Application Approval list. 
 *
 * @author cmc.nttruong1
 */

export default function POSMGoodsApproval() {
  ViewBase.call(this);

  const DEFAULT_PAGINATION = {
    pageSize: 10,
    pageIndex: 1,
    total: 0
  }
  const DEFAULT_FILTERS = {
    warehouse: { code: null, name: null },
    storageLocation: { code: null, name: null },
    main: { code: null, name: null },
    goodsType: { code: null, name: null },
    division: { code: null, name: null },
    model: { code: null, name: null },
    basicModel: { code: null, name: null },
    currentInputGoodTypeOrder: 0,
    channelCode: [],
    approvalStatusCode: '',
    confirmGoodsIssue: null,
    users: [],
    dateFrom: moment().subtract(1, 'M').startOf('M').format(formats.apiDateFormat),
    dateTo: moment().format(formats.apiDateFormat),
  };
  const CONFIRM_OPTIONS = [
    { code: true, name: 'YES', },
    { code: false, name: 'NO' },
  ]

  let approvalStatusOptions = [];
  let searchFilters = structuredClone(DEFAULT_FILTERS);
  let pagination = structuredClone(DEFAULT_PAGINATION);
  let currentFilter = structuredClone(DEFAULT_FILTERS);
  let selectedApproval = false;
  let tableDataHasChanged = new Map();
  let tableData = [];

  const requestParams = {
    get pageIndex() {
      return pagination.pageIndex
    },
    get pageSize() {
      return pagination.pageSize
    },
    get sortBy() {
      if (sortObj.sortBy)
        return `[${sortObj.sortBy}]`
    },
    get orderBy() {
      return sortObj.orderBy
    },
    get dateFrom() {
      return currentFilter.dateFrom
    },
    get dateTo() {
      return currentFilter.dateTo
    },
    get mainId() {
      return currentFilter.main.id || null
    },
    get goodsTypeId() {
      return currentFilter.goodsType.id || null
    },
    get divisionId() {
      return currentFilter.division.id || null
    },
    get modelId() {
      return currentFilter.model.id || null
    },
    get basicModelId() {
      return currentFilter.basicModel.id || null
    },
    get warehouseId() {
      return currentFilter.warehouse.code
    },
    get storageId() {
      return currentFilter.storageLocation.code
    },
    get channelCodes() {
      return currentFilter.channelCode.map(({ siteId }) => siteId).join('^')
    },
    get userIDs() {
      return currentFilter.users.map((user) => user.userID).join('^')
    },
    get approvalStatusCode() {
      return currentFilter.approvalStatusCode
    },
    get isConfirm() {
      return currentFilter.confirmGoodsIssue === '' ? null : currentFilter.confirmGoodsIssue
    },
  }

  const getTableDataList = () => {
    this.showLoading();
    setCurrentFilter();
    tableDataHasChanged.clear();

    getGoodsApprovalList(requestParams)
      .then((result) => {
        pagination.total = result.total || 0;
        pagination.pageIndex = result.pageIndex || 1;
        pagination.pageSize = result.pageSize || 10;
        tableData = result.data || [];
      })
      .catch((err) => { this.handleError(err); })
      .finally(() => { this.hideLoading() })
  }

  const onDownLoadExcel = () => {
    this.showLoading();
    let filename = `POSM_Goods_Approval_List_${moment().format('YYYYMMDDHHmmss')}_Report.xlsx`;

    downloadExcelGoodsApproval(requestParams, filename)
      .catch((error) => { this.handleError(error) })
      .finally(() => { this.hideLoading() })
  }

  const handleSaveApproval = () => {
    this.showLoading();

    let payload = [];

    for (const [movementControlId, isConfirm] of tableDataHasChanged.entries()) {
      payload.push({ movementControlId, isConfirm })
    }
    saveMultipleGoodsApproval(payload)
      .then((result) => {
        if (result) {
          this.showMessage(
            this.getText("EM00000762", "Changes have been saved."),
            this.getText("EM0000004", "Success"),
          );
        } else {
          this.showErrorMessage(result?.code ?? result?.message, this.getText("EM00000142", "Error"))
        }
      })
      .catch((error) => { this.handleError(error) })
      .finally(() => {
        getTableDataList();
        selectedApproval = null;
        tableDataHasChanged.clear();
        payload = [];
      })
  }

  const renderUpdateField = () => {
    let res = [
      m('.col-md-4.position-relative.g-1',
        m('label.form-label.fw-bold', { style: 'width: 35%' }, this.getText("LT00001267", "Goods Type")),
        m('label.form-label', selectedApproval.goodsTypeName)
      ),
      m('.col-md-4.position-relative.g-1',
        m('label.form-label.fw-bold', { style: 'width: 35%' }, this.getText("LT00000233", "Division")),
        m('label.form-label', selectedApproval.divisionName)
      ),
      m('.col-md-4.position-relative.g-1',
        m('label.form-label.fw-bold', { style: 'width: 35%' }, this.getText("LT00000717", "Model")),
        m('label.form-label', selectedApproval.modelName)
      ),
      m('.col-md-4.position-relative.g-1',
        m('label.form-label.fw-bold', { style: 'width: 35%' }, this.getText("LT00001268", "Basic Model")),
        m('label.form-label', selectedApproval.goodsName)
      ),
      m('.col-md-4.position-relative.g-1',
        m('label.form-label.fw-bold', { style: 'width: 35%' }, this.getText("LT00001272", "Control No")),
        m('label.form-label', selectedApproval.goodsControlId)
      ),
      m('.col-md-4.position-relative.g-1',
        m('label.form-label.fw-bold', { style: 'width: 35%' }, this.getText("LT00001273", "Total Qty")),
        m('label.form-label', selectedApproval.totalQty)
      ),
      m('.col-md-4.position-relative.g-1',
        m('label.form-label.fw-bold', { style: 'width: 35%' }, this.getText("LT00001269", "Warehouse")),
        m('label.form-label', selectedApproval.fromWarehouse)
      ),
      m('.col-md-4.position-relative.g-1',
        m('label.form-label.fw-bold', { style: 'width: 35%' }, this.getText("LT00001270", "Storage Location")),
        m('label.form-label', selectedApproval.fromStorage)
      ),
      m('.col-md-4.position-relative.g-1',
        m('label.form-label.fw-bold', { style: 'width: 35%' }, this.getText("LT00001277", "Storage Qty")),
        m('label.form-label', selectedApproval.storageQty)
      ),
      m('.col-md-4.position-relative.g-1',
        m('label.form-label.fw-bold', { style: 'width: 35%' }, this.getText("LT00001316", "Applicant")),
        m('label.form-label', selectedApproval.movementBy)
      ),
      m('.col-md-4.position-relative.g-1',
        m('label.form-label.fw-bold', { style: 'width: 35%' }, this.getText("LT00001256", "Goods Issue Date")),
        m('label.form-label', selectedApproval.goodsIssueDate)
      ),
      m('.col-md-4.position-relative.g-1',
        m('label.form-label.fw-bold', { style: 'width: 35%' }, this.getText("LT00001283", "Qty")),
        m('label.form-label', selectedApproval.movementQty)
      ),
      m('.col-md-4.position-relative.g-1',
        m('label.form-label.fw-bold', { style: 'width: 35%' }, this.getText("LT00001323", "Approve By")),
        m('label.form-label', selectedApproval.approveBy)
      ),
      m('.col-md-4.position-relative.g-1',
        m('label.form-label.fw-bold', { style: 'width: 35%' }, this.getText("LT00001324", "Approve By 2")),
        m('label.form-label', selectedApproval.approveBy2)
      ),
      m('.col-md-4.position-relative.g-1',
        m('label.form-label.fw-bold', { style: 'width: 35%' }, this.getText("LT00001322", "Approval Status")),
        m('label.form-label', selectedApproval.approvalStatus)
      ),
      m('.col-md-4.position-relative.g-1',
        m('label.form-label.fw-bold', { style: 'width: 35%' }, this.getText("LT00001263", "To Retail")),
        m('label.form-label', selectedApproval.channelName)
      ),
      m('.col-md-4.position-relative.g-1',
        m('label.form-label.fw-bold', { style: 'width: 35%' }, this.getText("LT00001260", "Retail Qty")),
        m('label.form-label', selectedApproval.channelQty)
      ),

      m('.col-md-4.position-relative.g-1',
        m('label.form-label.fw-bold',
          { style: 'width: 35%', for: 'GoodsIssueConfirm' },
          this.getText('LT00001310', 'Confirm Goods Issue')
        ),
        m(Checkbox, {
          id: 'GoodsIssueConfirm',
          style: 'cursor: default',
          readOnly: true,
          checked: selectedApproval.goodsIssueConfirm,
        })
      ),
    ]

    return res;
  }

  const validateCheckbox = (selectedApprovalStatusCode) => {
    return (selectedApprovalStatusCode?.toUpperCase() !== APPROVAL_STATUSES.CompletedSuccessfully.code.toUpperCase())
      || (!POSMAuthorizationModel.posmAuthority[POSM_PERMISSION_KEY.GOODS_ISSUE_CONFIRM])
  }

  const leavePageEventListener = (e) => {
    if (tableDataHasChanged.size > 0 && !confirm(this.getText('EM00000776', 'Changes you made may not be saved'))) {
      e.preventDefault();
      e.stopImmediatePropagation();
    }
  };

  const setSearchFilter = (searchFiltersPatch) => {
    searchFilters = { ...searchFilters, ...searchFiltersPatch }
  }
  const setCurrentFilter = () => {
    currentFilter = { ...searchFilters }
  }
  const setPagination = (paginationObjPatch) => {
    pagination = { ...pagination, ...paginationObjPatch };
    currentFilter = { ...currentFilter, ...pagination };
  }

  const handleClear = () => {
    searchFilters = structuredClone(DEFAULT_FILTERS);
    setCurrentFilter();
  }

  let sortObj = {
    sortBy: '',
    orderBy: '',
  }

  const _flipOrderText = (orderText) => {
    return orderText.toUpperCase() === DESCENDING_TEXT ? ASCENDING_TEXT : DESCENDING_TEXT
  }

  const _onSortClick = (sortBy) => {
    if (sortObj.sortBy === sortBy) {
      sortObj.orderBy = _flipOrderText(sortObj.orderBy)
    } else {
      sortObj.sortBy = sortBy;
      sortObj.orderBy = DESCENDING_TEXT;
    }
    getTableDataList();
  }

  const _getSortIcon = (sortBy) => {
    if (sortObj.sortBy !== sortBy) {
      return null
    }
    return sortObj.orderBy === DESCENDING_TEXT ? m('i.bi.bi-sort-down') : m('i.bi.bi-sort-up')
  }


  return {
    oninit: () => {
      approvalStatusOptions = Object.values(APPROVAL_STATUSES);
    },
    oncreate: () => {
      addEventListener('route', leavePageEventListener);
      addEventListener('beforeunload', leavePageEventListener);
    },
    onremove: () => {
      removeEventListener('route', leavePageEventListener);
      removeEventListener('beforeunload', leavePageEventListener);
    },
    view: () => {
      return [
        /* SEARCH */
        m('.card.mb-3',
          m('.card-body',
            m('.position-relative',
              m(StorageLocationSelect, {
                selectedItem: { storageLocation: searchFilters.storageLocation, warehouse: searchFilters.warehouse },
                onSelect: setSearchFilter
              }),
            ),
            m('hr'),
            m(MultiplePOSMLevelSelect, { selectedItems: searchFilters, onSelect: setSearchFilter }),
            m('.row.g-2.my-2',
              m('.col.position-relative',
                m('label.form-label', this.getText('LT00000132', 'Date')),
                m('.input-group',
                  m(DatePickerComponent, {
                    class: 'form-control',
                    isRange: true,
                    readonly: true,
                    grid: 1,
                    calendars: 1,
                    dateStart: searchFilters.dateFrom && moment(searchFilters.dateFrom).format(formats.dateFormat),
                    dateEnd: searchFilters.dateTo && moment(searchFilters.dateTo).format(formats.dateFormat),
                    required: true,
                    onselect: (e) => {
                      setSearchFilter({
                        dateFrom: moment(e.detail.start).format(formats.apiDateFormat),
                        dateTo: moment(e.detail.end).format(formats.apiDateFormat)
                      })
                    }
                  })
                )
              ),
              m('.col.position-relative',
                m('label.form-label', this.getText('LT00001245', 'Retail')),
                m('.input-group', m(SelectSiteSearchInputComponent, {
                  id: 'site',
                  selectedItems: searchFilters.channelCode,
                  multiple: true,
                  onSelect: (selectedItem) => {
                    setSearchFilter({ channelCode: selectedItem })
                  }
                }))
              ),
              m('.col.position-relative',
                m('label.form-label', this.getText("LT00001322", 'Approval Status')),
                m('.input-group',
                  m(SingleSelectComponent, {
                    popoverAttrs: { class: 'posm-filters' },
                    items: approvalStatusOptions,
                    selectedItem: searchFilters.approvalStatusCode,
                    checkmark: true,
                    filterable: false,
                    keyOnly: true,
                    onSelect: (s) => {
                      setSearchFilter({ approvalStatusCode: s })
                    },
                  })
                )
              ),
              m('.col.position-relative',
                m('label.form-label', this.getText('LT00001310', 'Confirm Goods Issue')),
                m('.input-group',
                  m(SingleSelectComponent, {
                    popoverAttrs: { class: 'posm-filters' },
                    items: CONFIRM_OPTIONS,
                    selectedItem: searchFilters.confirmGoodsIssue,
                    checkmark: true,
                    filterable: false,
                    keyOnly: true,
                    onSelect: (s) => {
                      setSearchFilter({ confirmGoodsIssue: s })
                    },
                  })
                )
              ),
              m('.col.position-relative',
                m('label.form-label', this.getText("LT00001316", 'Applicant')),
                m('.input-group',
                  m(SelectUserComponent, {
                    default: {
                      userStatusCode: '0001'
                    },
                    multiple: true,
                    onSelect: (selectedUserList) => {
                      setSearchFilter({ users: selectedUserList });
                    },
                    selectedUserList: searchFilters.users,
                  })
                )
              ),

              m('.col-12.d-flex.justify-content-end.gap-2',
                m('button.btn.btn-primary',
                  {
                    onclick: () => {
                      setPagination({ pageSize: 10, pageIndex: 1 });
                      getTableDataList();
                    },
                  },
                  this.getText('LT00000001', 'Search')
                ),
                m('button.btn.btn-white',
                  {
                    onclick: () => {
                      handleClear();
                    },
                  },
                  this.getText('LT00000031', 'Clear')
                )
              )
            ),
          )
        ),
        /* SEARCH */

        /* TABLE */
        m('.row.mb-3',
          m('.col-12',
            m('.card',
              m('.card-body',
                (tableData.length > 0) &&
                m('.d-flex.justify-content-end.gap-3.mb-2',
                  tableDataHasChanged.size > 0 &&
                  POSMAuthorizationModel.loadWithPOSMAuthority(
                    m('button.btn.btn-primary', { onclick: handleSaveApproval },
                      this.getText("LT00000022", "Save")
                    ),
                    POSM_PERMISSION_KEY.GOODS_ISSUE_CONFIRM
                  ),
                  m('a.btn.btn-primary',
                    {
                      onclick: () => {
                        onDownLoadExcel()
                      }
                    },
                    m('i.bi.bi-file-earmark-arrow-down-fill'), this.getText("LT00000018", "Excel Download"),
                  )
                ),
                m('.row',
                  m(".col-12.p-0",
                    m(".table-responsive",
                      m("table.table.table-hover.table-bordered", [
                        m("thead.thead-bggrey", { "style": "position : sticky ; top :1px; box-shadow: 0px -1px var(--is-link-blue)" },
                          m("tr.align-middle", { style: 'box-shadow: 0px 1px var(--blue-color-light)' }, [
                            m("th.text-center", { "scope": "col", "colspan": 3 }, this.getText("LT00001310", "Confirm Goods Issue")),
                            m("th.text-center", { "scope": "col", "colspan": 11 }, this.getText("LT00001309", "Goods Issue Application")),
                            m("th.text-center", { "scope": "col", "colspan": 4 }, this.getText("LT00001331", "Storage Application")),
                            m("th.text-center", { "scope": "col", "colspan": 3 }, this.getText("LT00001319", "Approval Condition")),
                          ]),
                          m("tr.align-middle",
                            m('th', this.getText("LT00001333", "Warehouse Issued")),
                            m('th.cursor-pointer', { onclick: () => { _onSortClick('movementControlId') } }, _getSortIcon('movementControlId'), this.getText("LT00001318", "Application ID")),
                            m('th.cursor-pointer', { onclick: () => { _onSortClick('goodsIssueDate') } }, _getSortIcon('goodsIssueDate'), this.getText("LT00001256", "Goods Issue Date")),

                            m('th.cursor-pointer', { onclick: () => { _onSortClick('goodsTypeName') } }, _getSortIcon('goodsTypeName'), this.getText("LT00001267", "Goods Type")),
                            m('th.cursor-pointer', { onclick: () => { _onSortClick('divisionName') } }, _getSortIcon('divisionName'), this.getText("LT00000233", "Division")),
                            m('th.cursor-pointer', { onclick: () => { _onSortClick('modelName') } }, _getSortIcon('modelName'), this.getText("LT00000717", "Model")),
                            m('th.cursor-pointer', { onclick: () => { _onSortClick('goodsName') } }, _getSortIcon('goodsName'), this.getText("LT00001268", "Basic Model")),
                            m('th.cursor-pointer', { onclick: () => { _onSortClick('goodsControlId') } }, _getSortIcon('goodsControlId'), this.getText("LT00001272", "Control No")),
                            m('th.cursor-pointer', { onclick: () => { _onSortClick('movementBy') } }, _getSortIcon('movementBy'), this.getText("LT00001316", "Applicant")),
                            m('th.cursor-pointer', { onclick: () => { _onSortClick('fromWarehouse') } }, _getSortIcon('fromWarehouse'), this.getText("LT00001242", "From Warehouse")),
                            m('th.cursor-pointer', { onclick: () => { _onSortClick('fromStorage') } }, _getSortIcon('fromStorage'), this.getText("LT00001243", "From Storage Location")),
                            m('th.cursor-pointer', { onclick: () => { _onSortClick('channelCode') } }, _getSortIcon('channelCode'), this.getText("LT00001244", "Retail Code")),
                            m('th.cursor-pointer', { onclick: () => { _onSortClick('channelName') } }, _getSortIcon('channelName'), this.getText("LT00001245", "Retail")),
                            m('th.cursor-pointer', { onclick: () => { _onSortClick('movementQty') } }, _getSortIcon('movementQty'), this.getText("LT00001283", "Qty")),

                            m('th.cursor-pointer', { onclick: () => { _onSortClick('totalQty') } }, _getSortIcon('totalQty'), this.getText("LT00001273", "Total Qty")),
                            m('th.cursor-pointer', { onclick: () => { _onSortClick('storageQty') } }, _getSortIcon('storageQty'), this.getText("LT00001277", "Storage Qty")),
                            m('th.cursor-pointer', { onclick: () => { _onSortClick('pendingIssueQty') } }, _getSortIcon('pendingIssueQty'), this.getText("LT00001328", "Pending Issue Qty")),
                            m('th.cursor-pointer', { onclick: () => { _onSortClick('channelQty') } }, _getSortIcon('channelQty'), this.getText("LT00001260", "Retail Qty")),

                            m('th.cursor-pointer', { onclick: () => { _onSortClick('approveBy') } }, _getSortIcon('approveBy'), this.getText("LT00001323", "Approve By")),
                            m('th.cursor-pointer', { onclick: () => { _onSortClick('approveBy2') } }, _getSortIcon('approveBy2'), this.getText("LT00001324", "Approve By 2")),
                            m('th.cursor-pointer', { onclick: () => { _onSortClick('approvalStatus') } }, _getSortIcon('approvalStatus'), this.getText("LT00001322", "Approval Status")),
                          )
                        ),
                        m("tbody",
                          (tableData.length == 0) &&
                          m("tr", { "class": "align-middle" },
                            m("td", { "colspan": 28 }, this.getText("EM00000141", "No records found."))
                          ),
                          (tableData.length > 0) &&
                          tableData.map((row) =>
                            m("tr.align-middle",
                              m('td.text-center',
                                m(Checkbox, {
                                  checked: tableDataHasChanged.has(row.movementControlId) ? tableDataHasChanged.get(row.movementControlId) : row.goodsIssueConfirm,
                                  disabled: validateCheckbox(row.approvalStatusCode),
                                  onchange: (e) => {
                                    tableDataHasChanged.set(row.movementControlId, e.target.checked)
                                  }
                                })
                              ),
                              m('td',
                                m('a.btn.btn-link',
                                  {
                                    onclick: () => {
                                      selectedApproval = structuredClone(row);
                                    },
                                  },
                                  row.movementControlId
                                ),
                              ),
                              m('td', row.goodsIssueDate),

                              m('td', row.goodsTypeName),
                              m('td', row.divisionName),
                              m('td', row.modelName),
                              m('td', row.goodsName),
                              m('td', row.goodsControlId),
                              m('td', row.movementBy),
                              m('td', row.fromWarehouse),
                              m('td', row.fromStorage),
                              m('td', row.channelCode),
                              m('td', row.channelName),
                              m('td', row.movementQty),

                              m('td', row.totalQty),
                              m('td', row.storageQty),
                              m('td', row.pendingIssueQty),
                              m('td', row.channelQty),

                              m('td', row.approveBy),
                              m('td', row.approveBy2),
                              m('td', row.approvalStatus),
                            )
                          )
                        )
                      ])
                    ),
                    (tableData.length > 0) &&
                    m(".px-3",
                      m(paginationComponent, {
                        currentPage: pagination.pageIndex,
                        pageSize: pagination.pageSize,
                        totalItems: pagination.total,
                        onPageChanged: (pageIndex) => {
                          setPagination({ pageIndex })
                          getTableDataList();
                        },
                        onPageSizeChanged: (pageIndex, pageSize) => {
                          setPagination({ pageIndex, pageSize });
                          getTableDataList();
                        },
                      })
                    )
                  )
                )
              )
            )
            //End List
          ),
        ),
        /* TABLE */

        /*  GOODS ISSUE APPROVE SECTION */
        selectedApproval &&
        m('.card',
          m('.card-header.ps-3.d-flex.justify-content-between.gap-2', { style: "border: none" },
            m('h2.secondary-title.card-title', this.getText('LT00001310', 'Confirm Goods Issue') + ' (' + selectedApproval.movementControlId + ')'),
            m('.d-flex.justify-self-end.gap-2',
              m('button.btn.btn-white',
                { onclick: () => { selectedApproval = null; } },
                this.getText("LT00000021", "Close")
              )
            ),
          ),
          m('.card-body',
            m('.row.g-2',
              renderUpdateField()
            ),
          )
        ),
        /*  GOODS ISSUE APPROVE SECTION */

        this.getBaseViews(),
      ];
    },
  };
}
